import React from 'react';
import { Box, FormControl, Select, MenuItem, Tabs, Tab, Paper } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { END_PIER_1_TAB_OPTIONS } from './EndPier1Board';
import { END_PIER_4_TAB_OPTIONS } from './EndPier4Board';

const pylon23Asset = (fileName) =>
  `/${encodeURIComponent('pylon 2, 3')}/${encodeURIComponent(fileName)}`;

const Monitor = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const monitorOptions = [
    { value: '/monitor/end-pier-1', label: 'End Pier 1' },
    { value: '/monitor/end-pier-4', label: 'End Pier 4' },
    { value: '/monitor/superstructure', label: 'Upper Superstructure' },
    { value: '/monitor/tower1', label: 'Lower Superstructure' },
    { value: '/monitor/pylon2-left-leg', label: 'Pylon 2 Left Leg' },
    { value: '/monitor/pylon2-right-leg', label: 'Pylon 2 Right Leg' },
    { value: '/monitor/pylon3-left-leg', label: 'Pylon 3 Left Leg' },
    { value: '/monitor/pylon3-right-leg', label: 'Pylon 3 Right Leg' }
  ];
  const pylonTabOptions = [
    { value: 'Navigation', label: 'Navigation' },
    { value: 'Elev 768.9', label: 'ELE 768.9' },
    { value: 'A', label: 'ELE 760' },
    { value: 'B', label: 'ELE 748' },
    { value: 'C', label: 'ELE 730' },
    { value: 'D', label: 'ELE 689' },
    { value: 'Elev 601.9', label: 'ELE 601.9' },
    { value: 'Elev 529.5', label: 'ELE 529.5' },
    { value: 'Elev 469.6', label: 'ELE 469.6' }
  ];
  const isEndPier1Route = location.pathname === '/monitor/end-pier-1';
  const isEndPier4Route = location.pathname === '/monitor/end-pier-4';
  const isPylonRoute = location.pathname.includes('/monitor/pylon2-')
    || location.pathname.includes('/monitor/pylon3-')
    || isEndPier1Route
    || isEndPier4Route;
  const structureTabOptions = (() => {
    if (isEndPier4Route) return END_PIER_4_TAB_OPTIONS;
    if (isEndPier1Route) return END_PIER_1_TAB_OPTIONS;
    return pylonTabOptions;
  })();

  // Get current selection based on route
  const getCurrentValue = () => {
    const current = monitorOptions.find(opt => opt.value === location.pathname);
    return current ? current.value : '';
  };

  const handleMonitorChange = (event) => {
    if (!event.target.value) {
      navigate('/monitor');
      return;
    }
    navigate(event.target.value);
  };
  const searchParams = new URLSearchParams(location.search);
  const selectedPylonTab = searchParams.get('tab') || 'Navigation';
  const handlePylonTabChange = (event, newValue) => {
    const next = new URLSearchParams(location.search);
    next.set('tab', newValue);
    navigate(`${location.pathname}?${next.toString()}`, { replace: true });
  };

  return (
    <Box sx={{ 
      pt: '80px', // Space for header (60px) + some padding
      px: 3,
      pb: 3,
      minHeight: 'calc(100vh - 80px)',
      bgcolor: 'white'
    }}>
      {/* Monitor dropdown with pylon options bar to its right */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
        mb: 3,
        mt: 2
      }}>
        <FormControl sx={{ minWidth: 200 }}>
          <Select
            id="monitor-select"
            value={getCurrentValue()}
            onChange={handleMonitorChange}
            displayEmpty
            inputProps={{ 'aria-label': 'Monitor select' }}
            renderValue={(selected) => {
              if (!selected) return 'Select';
              const current = monitorOptions.find((opt) => opt.value === selected);
              return current ? current.label : 'Select';
            }}
            sx={{
              bgcolor: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ddd'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2'
              }
            }}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {monitorOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isPylonRoute && (
          <Paper elevation={1} sx={{ px: 1, py: 0.25, flex: 1, minWidth: 500 }}>
            <Tabs
              value={structureTabOptions.some((tab) => tab.value === selectedPylonTab) ? selectedPylonTab : 'Navigation'}
              onChange={handlePylonTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 34,
                  fontSize: '0.95rem'
                }
              }}
            >
              {structureTabOptions.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </Paper>
        )}

      </Box>

      {/* Content Area */}
      <Box>
        {location.pathname === '/monitor' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: 'stretch',
              justifyContent: 'center',
              maxWidth: 1800,
              mx: 'auto'
            }}
          >
            {[
              { src: pylon23Asset('front view.png'), alt: 'Front view' },
              { src: pylon23Asset('front_view 2 .png'), alt: 'Front view 2' }
            ].map(({ src, alt }) => (
              <Box
                key={alt}
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                  width: '100%',
                  height: { xs: 420, md: '78vh' },
                  maxHeight: { xs: 480, md: '78vh' },
                  overflow: 'hidden',
                  borderRadius: 1,
                  boxShadow: 1,
                  bgcolor: '#f5f5f5'
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={alt}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center center',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.currentTarget.src = '/new_bridge_rendering.png';
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
        <Outlet />
      </Box>
    </Box>
  );
};

export default Monitor;
